<template>
  <div class="store">
    <div class="store__top">
      <div class="container">
        <div class="store__top-wrapper">
          <el-breadcrumb separator=">">
            <el-breadcrumb-item :to="translations.i18nRoute({ name: 'home' })">{{ t('home.title') }}</el-breadcrumb-item>
            <el-breadcrumb-item>{{ categoryBySlug.name }}</el-breadcrumb-item>
          </el-breadcrumb>
          <h1 class="store__top-title">{{ categoryBySlug.name }}</h1>
          <div class="store__top-subtitle" v-html="categoryBySlug.description" />
          <div class="store__top-actions">
            <div class="store__top-block">
              <input
                  type="text"
                  :placeholder="t('fields.search')"
                  v-model="searchInput"
              >
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="vuesax/broken/search-normal">
                  <g id="search-normal">
                    <path id="Vector"
                        d="M11.9791 2.08325C17.4478 2.08325 21.8749 6.51034 21.8749 11.9791C21.8749 17.4478 17.4478 21.8749 11.9791 21.8749C6.51034 21.8749 2.08325 17.4478 2.08325 11.9791C2.08325 8.12492 4.28117 4.79159 7.49992 3.15617"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path id="Vector_2" d="M22.9166 22.9166L20.8333 20.8333" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round"/>
                  </g>
                </g>
              </svg>
            </div>
            <div class="store__top-filter">
              <div>
                <img src="@icons/filterIcon.svg" alt="filter">
              </div>
              <div @click="setNameOrdering">
                {{ t('store.filter.name') }}
                <span class="store__top-button">
                  <span
                      :class="orderBy === 'name' ? 'store__top-button-small' : 'store__top-button-active'"
                  />
                  <span />
                  <span
                      :class="orderBy === 'name' ? 'store__top-button-active' : 'store__top-button-small'"
                  />
                </span>
              </div>
              <div @click="setPriceOrdering">
                {{ t('store.filter.price') }}
                <span class="store__top-button">
                  <span
                      :class="orderBy === 'price' ? 'store__top-button-small' : 'store__top-button-active'"
                  />
                  <span />
                  <span
                      :class="orderBy === 'price' ? 'store__top-button-active' : 'store__top-button-small'"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="store__main">
      <div class="container">
        <div class="store__list" v-if="products.length > 0">
          <product-card
              v-for="product in products"
              :key="product.node.uuid"
              :product="product.node"
          />
          <div class="store__list-observer" ref="observer"></div>
        </div>
        <div class="store__list" v-else-if="isLoading">
          <product-card-skeleton v-for="idx in 12" :key="idx" />
        </div>
        <p v-else class="store__noProducts">{{ t('store.noProducts') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import translations from "@/core/helpers/translations.js";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {computed, onMounted, ref, watch} from "vue";
import ProductCard from "@/components/cards/product-card.vue";
import {useIntersectionObserver} from "@vueuse/core";
import {Products} from "@/core/api/products.js";
import {debounce} from "@/core/helpers/debounce.js";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import {Categories} from "@/core/api/categories.js";

const {t, locale} = useI18n()
const route = useRoute()
const router = useRouter()

const category = computed(() => {
  return route.params.category
})

const isLoading = ref(false)
const observer = ref(null)

const { stop } = useIntersectionObserver(
  observer,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting && pageInfo.value.hasNextPage) {
      getMoreProducts()
    }
  },
)

const toggleOrdering = (type) => {
  const currentType = orderBy.value.startsWith('-') ? orderBy.value.slice(1) : orderBy.value;
  orderBy.value = (currentType === type) ? (orderBy.value.startsWith('-') ? type : `-${type}`) : type;
};
const setPriceOrdering = () => toggleOrdering('price');
const setNameOrdering = () => toggleOrdering('name');

const searchInput = ref(route.query['name'] ? (route.query['name']) : '')
const products = ref([])
const pageInfo = ref([])
const cursorForAfter = ref('')
const name = ref(route.query['name'] ? (route.query['name']) : '')
const orderBy = ref(route.query['orderBy'] ? (route.query['orderBy']) : 'price')

const query = computed(() => {
  let res =`?category=${category.value}&orderBy=${orderBy.value}`

  if (name.value) {
    res = res + `&name=${name.value}`
  }

  return res
})

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  if (category.value) {
    const res = await Products.getProducts(
        `after: "${cursorForAfter.value}"` +
        `, first: 12` +
        `, categoriesSlugs: "${category.value}"` +
        `, orderBy: "${orderBy.value}"` +
        (name.value ? `, name: "${name.value}"` : "")
    )
    products.value = res.data?.products?.edges
    pageInfo.value = res.data?.products?.pageInfo

    isLoading.value = false
  } else {
    await router.push({
      name: 'home',
      params: {
        locale: localStorage.getItem('fpt-user-locale') || 'en-gb'
      }
    })
  }
}

const getMoreProducts = async () => {
  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 12` +
      `, categoriesSlugs: "${category.value}"` +
      `, orderBy: "${orderBy.value}"` +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

watch(
  () => query.value,
  async () => {
    products.value = [];
    history.pushState({}, '', route.path + query.value);
    await getProducts();
  }
)

const updateNameDebounced = debounce(() => {
  name.value = searchInput.value;
}, 500);
watch(
  () => searchInput.value,
  () => {
    updateNameDebounced();
  }
)

const categoryBySlug = ref({})

const getCategory = async () => {
  const res = await Categories.getCategoryBySlug(category.value)
  categoryBySlug.value = res.data.categories.edges[0].node
}

watch(
    () => locale.value,
    async () => {
      products.value = [];
      history.pushState({}, '', route.path + query.value);
      await getCategory()
      await getProducts()
    }
)

watch(
    () => category.value,
    async () => {
      products.value = [];
      history.pushState({}, '', route.path + query.value);
      await getCategory()
      await getProducts()
    }
)

onMounted( async () => {
  await getCategory()
  await getProducts()
})
</script>

<style lang="scss" scoped>
.store {
  position: relative;

  &__wave {
    width: 100vw;
    position: absolute;
  }

  &__top {
    position: relative;
    z-index: 1;
    padding-block: 50px 80px;

    &-title {
      margin-top: 20px;
      text-transform: capitalize;
      font-family: Syne, sans-serif;
      font-size: 50px;
      font-weight: 700;
      background: linear-gradient(90deg, #FF85FD 0.5%, #7D045A 44.83%, #3D2B44 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    &-subtitle {
      width: 1000px;
      margin-top: 30px;
      color: #696969;
      font-size: 22px;
      font-weight: 500;
    }

    &-actions {
      margin-top: 50px;
      display: flex;
      align-items: center;
      gap: 25px;
    }

    &-block {
      position: relative;

      & input {
        width: 550px;
        border-radius: 8px;
        border: 1px solid #5B194F;
        background-color: $white;
        box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.15);
        padding: 13px 20px;
      }

      & svg {
        position: absolute;
        z-index: 1;
        right: 17px;
        top: 50%;
        transform: translateY(-50%);
        color: #233251;
      }
    }

    &-filter {
      display: flex;
      align-items: center;
      border-radius: 8px;
      border: 1px solid #5B194F;
      background-color: $white;
      box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.15);
      height: 44px;

      & div {
        cursor: pointer;
        padding-inline: 20px;
        height: 100%;
        border-right: 1px solid #5B194F;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;

        color: #233251;
        text-align: center;
        font-size: 16px;
        font-weight: 500;

        &:first-child {
        cursor: default;
        }

        &:last-child {
          border-right: none;
        }
      }
    }

    &-button {
      display: flex;
      align-items: start;
      flex-direction: column;
      justify-content: space-between;
      width: 30px;
      height: 20px;

      & span {
        height: 3px;
        background-color: #233251;
        border-radius: 0.5rem;
        width: 75%;
        transition: all 0.3s ease;
      }

      &-small {
        width: 50% !important;
      }

      &-active {
        width: 100% !important;
      }
    }
  }

  &__main {
    //background-color: rgba(157, 231, 255, 0.54);
    background-image: url(@images/storeBg.png);
    background-size: cover;
    background-repeat: repeat;
    padding-block: 75px;
    position: relative;
    overflow: hidden;

    &-bg {
      width: 100%;
      position: absolute;
      left: 0;
      top: 0;
    }
  }

  &__list {
    position: relative;
    z-index: 1;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 359px);
    justify-content: space-between;
    row-gap: 50px;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }

  &__noProducts {
    text-align: center;
    font-size: 24px;
    font-weight: 500;
  }
}

@media (max-width: 1250px) {
  .store {
    &__list {
      grid-template-columns: repeat(2, 359px);
      column-gap: 50px;
      justify-content: center;
    }
  }
}

@media (max-width: 1050px) {
  .store__top {
    &-subtitle {
      width: 100%;
    }

    &-actions {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

@media (max-width: 850px) {
  .store {
    &__list {
      grid-template-columns: repeat(1, 359px);
    }

    &__top {
      &-subtitle {
        font-size: 16px;
      }

      &-actions {
        align-items: center;
      }

      &-block {
        width: 312px;

        & input {
          width: 100%;
        }
      }
    }
  }
}
</style>